import React, { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode, Ref } from 'react';
import { Link } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Glass } from '@samasante/liquid-glass';
import type { GlassOptics } from '@samasante/liquid-glass';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import clsx from 'clsx';

import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'secondary_base' | 'secondary_emphasized' | 'tertiary' | 'glassTertiary' | 'danger' | 'hidden' | 'row';

export type ButtonSize = 'tight' | 'fit' | 'small';

type NativeButtonType = NonNullable<ComponentPropsWithoutRef<'button'>['type']>;

type ButtonElement = HTMLButtonElement | HTMLAnchorElement;

export interface ButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'type'> {
    type?: NativeButtonType | 'link' | 'reactLink';
    caption?: ReactNode;
    iconName?: string;
    dataTestId?: string;
    variant?: ButtonVariant;
    size?: ButtonSize;
    raised?: string | boolean;
    negativeMargin?: boolean;
    textCenter?: boolean;
    mobileFullwidth?: boolean;
    href?: string;
    rel?: string;
    target?: string;
    to?: LinkProps['to'];
}

const variantClass: Record<ButtonVariant, string> = {
    primary: styles.primary,
    secondary: styles.secondary,
    secondary_base: styles.secondaryBase,
    secondary_emphasized: styles.secondaryEmphasized,
    tertiary: styles.tertiary,
    glassTertiary: styles.glassTertiary,
    danger: styles.danger,
    hidden: styles.hidden,
    row: styles.row,
};

const GLASS_OPTICS: Partial<GlassOptics> = {
    mapSize: 256,
    frost: 6,
    saturate: 1.2,
    strength: 0.06,
    depth: 0.35,
    curvature: 0.2,
    bend: 0.45,
    bendWidth: 0.14,
    dispersion: 0.3,
    sheen: 0.35,
    sheenWidth: 2,
    glow: 0.12,
    brightness: 0.06,
};

const sizeClass: Record<ButtonSize, string | undefined> = {
    tight: styles.tight,
    fit: undefined,
    small: styles.small,
};

export const Button = forwardRef<ButtonElement, ButtonProps>(
    (
        {
            type,
            dataTestId,
            caption,
            iconName,
            children,
            to,
            variant,
            size,
            raised,
            negativeMargin,
            textCenter,
            mobileFullwidth,
            hidden,
            disabled,
            className,
            ...rest
        },
        ref,
    ) => {
        const classes = clsx(
            styles.button,
            variantClass[variant ?? 'primary'],
            size && sizeClass[size],
            raised && styles.raised,
            negativeMargin && styles.negativeMargin,
            textCenter && styles.textCenter,
            mobileFullwidth && styles.mobileFullwidth,
            hidden && styles.invisible,
            disabled && styles.disabled,
            className,
        );
        const icon = iconName && (
            <span className={styles.iconContainer}>
                <Icon name={iconName} size="24px" leftIndent="8px" />
            </span>
        );

        let element;
        if (type === 'link') {
            const linkProps = rest as ComponentPropsWithoutRef<'a'>;
            element = (
                <a ref={ref as Ref<HTMLAnchorElement>} data-testid={dataTestId} className={classes} {...linkProps}>
                    {caption}
                    {icon}
                </a>
            );
        } else if (type === 'reactLink') {
            const routerLinkProps = rest as Omit<LinkProps, 'to'>;
            element = (
                <Link ref={ref as Ref<HTMLAnchorElement>} data-testid={dataTestId} to={to ?? '#'} className={classes} {...routerLinkProps}>
                    {caption}
                    {icon}
                </Link>
            );
        } else {
            element = (
                <button ref={ref as Ref<HTMLButtonElement>} type={type} data-testid={dataTestId} disabled={disabled} className={classes} {...rest}>
                    {caption}
                    {children}
                    {icon}
                </button>
            );
        }

        return variant === 'glassTertiary' ? (
            <Glass className={clsx(styles.glassLens, mobileFullwidth && styles.mobileFullwidth)} optics={GLASS_OPTICS}>
                {element}
            </Glass>
        ) : (
            element
        );
    },
);

Button.propTypes = {
    type: PropTypes.oneOf(['button', 'submit', 'link', 'reactLink']),
    variant: PropTypes.oneOf(['primary', 'secondary', 'secondary_base', 'secondary_emphasized', 'tertiary', 'glassTertiary', 'danger', 'hidden', 'row']),
    size: PropTypes.oneOf(['tight', 'fit', 'small']),
    raised: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    disabled: PropTypes.bool,
    href: PropTypes.string,
    to: PropTypes.string,
    target: PropTypes.string,
    rel: PropTypes.string,
    title: PropTypes.string,
    tabIndex: PropTypes.number,
    form: PropTypes.string, // для того, чтобы можно было связывать кнопку submit по id с form, если она находится вне form (н-р в попапах)
    caption: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    dataTestId: PropTypes.string, // для html аттрибута data-testid (babel plugin "react-remove-properties")
    negativeMargin: PropTypes.bool, // чтобы выравнивать row-кнопки по контенту
    onClick: PropTypes.func,
    textCenter: PropTypes.bool,
    mobileFullwidth: PropTypes.bool, // 100% ширины до брейкпоинта tablet720
    iconName: PropTypes.string,
};
